import { AdminGetRoutesLocationsResDto } from "service/api/estabrookSchemas";
import { TimeAdjustments } from "../../../../hooks/use-realtime-time-adjustments";
import { ZERO_SERVICE_TIME_MIN_WIDTH } from "../../../../shared/utils/address-box-styling";
import { TIMETABLE_ITEM_WIDTH } from "./timetable";
import { RouteStatus } from "./route-status.enum";

// Statuses that indicate the driver is done with the location
// (mirrors the done-status handling in location-address.tsx)
const DONE_STATUSES = ["completed", "partiallyCompleted", "cantPlow"];

// Minimum visible strip of a partially covered completed box in a stack
const STACK_SLIVER_WIDTH = 7;

/**
 * Effective service time as rendered on the timeline.
 *
 * In live phase, use the actual service time calculated from travelDetails;
 * otherwise fall back to the location's serviceTime. Adjusted service time
 * takes precedence. Admin-completed live orders do not always receive an
 * actual service-time value from the driver flow, so they are treated as
 * zero-duration, the same as driver-completed zero-duration orders.
 *
 * Shared between DraggableOrder (rendering) and computeCompletedStackShifts
 * (overlap detection) so the two cannot drift.
 */
export const getEffectiveServiceTime = (
  order: AdminGetRoutesLocationsResDto | undefined,
  actualServiceTime: number | undefined,
  adjustedServiceTime: number | undefined,
  currentRouteStatus?: RouteStatus,
): number | undefined => {
  const isCantPlow = order?.driverStatus === "cantPlow";
  const isAdminCompleted =
    order?.driverStatus === "completed" &&
    order?.driverStatusLastUpdatedBy === "admin";
  const liveServiceTime =
    actualServiceTime !== undefined
      ? actualServiceTime
      : isAdminCompleted
        ? 0
        : undefined;

  return (
    adjustedServiceTime ??
    (currentRouteStatus === RouteStatus.Live && liveServiceTime !== undefined
      ? liveServiceTime
      : isCantPlow && actualServiceTime === 0
        ? 0 // For cantPlow with 0 actual service time, use 0 (not original serviceTime)
        : order?.serviceTime)
  );
};

/**
 * Computes a leftward translateX (px) per location so that consecutive done
 * zero-service-time stops don't paint on top of each other.
 *
 * Done zero-service boxes render 20px wide but consume 0px of timeline flow
 * (marginLeft: -boxWidth in location-address.tsx), so each box's right edge
 * sits at its time position. When the pixel gap between consecutive stops is
 * smaller than the box width — ~0 when an admin bulk-completes addresses —
 * the boxes fully overlap. Shifting each older box left just enough to keep a
 * STACK_SLIVER_WIDTH strip visible produces a deck-of-cards stagger. The
 * shift is applied via CSS transform, so timeline flow is unaffected.
 *
 * Every member of a stack gets an entry — the top (newest) card with shift 0 —
 * so the renderer can style all stacked cards (fill, shadow), not just the
 * shifted ones.
 */
export const computeCompletedStackShifts = (
  items: AdminGetRoutesLocationsResDto[],
  opts: {
    adjustments?: Record<string, TimeAdjustments>;
    actualServiceTimeMap?: Record<string, number | undefined>;
    zoomLevel: number;
    currentRouteStatus?: RouteStatus;
  },
): Record<string, number> => {
  const { adjustments, actualServiceTimeMap, zoomLevel, currentRouteStatus } =
    opts;
  const pixelsPerMinute = (TIMETABLE_ITEM_WIDTH / 60) * zoomLevel;

  const isDoneZeroService = (item: AdminGetRoutesLocationsResDto): boolean => {
    if (!DONE_STATUSES.includes(item.driverStatus || "")) return false;
    const effectiveServiceTime = getEffectiveServiceTime(
      item,
      actualServiceTimeMap?.[item.locationId],
      adjustments?.[item.locationId]?.adjustedServiceTime,
      currentRouteStatus,
    );
    return effectiveServiceTime === 0;
  };

  const shifts: Record<string, number> = {};
  // Walk right-to-left: the newest box of a run keeps its true time position;
  // each older box shifts left until its uncovered strip reaches the sliver.
  let nextShift = 0;
  let nextIsStacked = false;

  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];

    if (!isDoneZeroService(item)) {
      // A done stop with real width (e.g. driver-started, admin-completed)
      // can still be overlapped by the following card's leftward overhang.
      // Mark that card as a stack member so it renders as a proper card
      // (opaque fill, shadow, hover raise) instead of a transparent tangle;
      // the wide box itself stays visible via its z-index raise in
      // location-address.tsx.
      const nextItem = items[i + 1];
      if (
        nextItem &&
        DONE_STATUSES.includes(item.driverStatus || "") &&
        isDoneZeroService(nextItem)
      ) {
        const travelTime =
          adjustments?.[item.locationId]?.adjustedTravelTime ??
          nextItem.travelDetails?.travelTimeBetweenLocations ??
          0;
        const gapPx = travelTime * pixelsPerMinute;
        const overhangPx = ZERO_SERVICE_TIME_MIN_WIDTH + nextShift - gapPx;
        if (overhangPx > 0) {
          shifts[nextItem.locationId] ??= nextShift;
        }
      }
      nextShift = 0;
      nextIsStacked = false;
      continue;
    }

    if (!nextIsStacked) {
      nextShift = 0;
      nextIsStacked = true;
      continue;
    }

    // Gap between this box's right edge and the next box's right edge is the
    // travel time between them (same source as DraggableOrder's travel line).
    const travelTime =
      adjustments?.[item.locationId]?.adjustedTravelTime ??
      items[i + 1]?.travelDetails?.travelTimeBetweenLocations ??
      0;
    const gapPx = travelTime * pixelsPerMinute;

    // Uncovered strip of this box is naturally gapPx − shift(next); shift this
    // box left by whatever is missing to reach the sliver width.
    const shift = Math.max(0, STACK_SLIVER_WIDTH - gapPx + nextShift);
    const uncoveredPx = gapPx - nextShift + shift;

    if (uncoveredPx < ZERO_SERVICE_TIME_MIN_WIDTH) {
      // This box is partially covered by the next one: both are stack members.
      // The next box keeps whatever shift it already has (0 marks the top card).
      shifts[item.locationId] = shift;
      shifts[items[i + 1].locationId] ??= 0;
    }
    nextShift = shift;
  }

  return shifts;
};
